import { useState } from "react";
import { MessageCircle, Send, X, Phone, Sparkles, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import { motion, AnimatePresence } from "framer-motion";

const smsSchema = z.object({
  name: z.string().trim().max(100).optional(),
  phone: z.string().trim().min(10, "Please enter a valid phone number").max(15),
  message: z.string().trim().min(1, "Please enter a message").max(500),
  transactionalConsent: z.literal(true, { errorMap: () => ({ message: "Consent is required" }) }),
  marketingConsent: z.boolean().optional(),
});

interface SMSFormData {
  name: string;
  phone: string;
  message: string;
  transactionalConsent: boolean;
  marketingConsent: boolean;
}

export const TextUsButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [formData, setFormData] = useState<SMSFormData>({
    name: "",
    phone: "",
    message: "",
    transactionalConsent: true,
    marketingConsent: false,
  });
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = smsSchema.safeParse(formData);
    if (!result.success) {
      toast({
        title: "Missing Information",
        description: result.error.errors[0].message,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Temporarily send as email while waiting for Twilio A2P 10DLC registration
      const { error } = await supabase.functions.invoke("send-lead-notification", {
        body: {
          leadType: 'contact',
          name: formData.name || undefined,
          email: `text-reply@${formData.phone.replace(/\D/g, '')}.sms`, // Placeholder email with phone
          phone: formData.phone,
          message: `📱 TEXT MESSAGE FROM CUSTOMER\n\nPhone: ${formData.phone}\n${formData.name ? `Name: ${formData.name}\n` : ''}\nMessage:\n${formData.message}\n\n---\nReply via text to: ${formData.phone}`,
          sourcePage: window.location.pathname,
          region: 'texas',
        },
      });

      if (error) throw error;

      setIsSent(true);
      setTimeout(() => {
        setFormData({ name: "", phone: "", message: "", transactionalConsent: true, marketingConsent: false });
        setIsOpen(false);
        setIsSent(false);
      }, 2500);
    } catch (error: any) {
      console.error("SMS error:", error);
      toast({
        title: "Couldn't Send Message",
        description: "Please call us instead at 469-404-0899",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatPhoneInput = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (digits.length >= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    if (digits.length >= 3) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return digits;
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsSent(false);
  };

  return (
    <>
      <motion.button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-40 bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-500 hover:to-amber-400 text-white px-5 py-3 rounded-xl shadow-xl shadow-orange-600/25 flex items-center gap-2.5 font-semibold border border-orange-400/20"
        whileHover={{ scale: 1.03, y: -2 }}
        whileTap={{ scale: 0.98 }}
        aria-label="Text Us"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="text-sm tracking-wide">Text Us</span>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={handleClose}
            />

            <motion.div 
              className="relative bg-gradient-to-b from-[#1e1e1e] to-[#151515] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-orange-500/20 max-h-[90vh] overflow-y-auto"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
            >
              <div className="h-1 bg-gradient-to-r from-orange-600 via-amber-500 to-orange-600" />

              <AnimatePresence mode="wait">
                {isSent ? (
                  <motion.div key="success" className="p-8 text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <motion.div 
                      className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                    >
                      <Sparkles className="w-10 h-10 text-white" />
                    </motion.div>
                    <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
                    <p className="text-gray-400">We'll text you back shortly.</p>
                  </motion.div>
                ) : (
                  <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <div className="p-6 pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-500/20">
                            <MessageCircle className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-white">Text Us</h3>
                            <p className="text-sm text-gray-400">Quick response guaranteed</p>
                          </div>
                        </div>
                        <button onClick={handleClose} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg" aria-label="Close">
                          <X className="w-5 h-5" aria-hidden="true" />
                        </button>
                      </div>
                    </div>

                    <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
                      <div className="space-y-1.5">
                        <label className="block text-sm font-medium text-gray-300">Your Name <span className="text-gray-500">(optional)</span></label>
                        <Input
                          type="text"
                          placeholder="John Smith"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 rounded-xl"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="block text-sm font-medium text-gray-300">Phone Number <span className="text-orange-400">*</span></label>
                        <Input
                          type="tel"
                          placeholder="(469) 555-1234"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: formatPhoneInput(e.target.value) })}
                          className="w-full h-12 bg-white/5 border-white/10 text-white placeholder:text-gray-500 rounded-xl"
                          required
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="block text-sm font-medium text-gray-300">Message <span className="text-orange-400">*</span></label>
                        <Textarea
                          placeholder="I'm interested in outdoor kitchens..."
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          className="w-full min-h-[80px] bg-white/5 border-white/10 text-white placeholder:text-gray-500 rounded-xl resize-none"
                          required
                        />
                      </div>

                      {/* Consent Checkboxes - TCPA Compliant */}
                      <div className="space-y-3 pt-2">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            id="transactional"
                            checked={formData.transactionalConsent}
                            onCheckedChange={(checked) => setFormData({ ...formData, transactionalConsent: checked === true })}
                            className="mt-0.5 border-gray-600 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                          />
                          <label htmlFor="transactional" className="text-xs text-gray-400 leading-relaxed cursor-pointer">
                            I agree to receive text messages about my inquiry (transactional). <span className="text-orange-400">*</span>
                          </label>
                        </div>

                        <div className="flex items-start gap-3">
                          <Checkbox
                            id="marketing"
                            checked={formData.marketingConsent}
                            onCheckedChange={(checked) => setFormData({ ...formData, marketingConsent: checked === true })}
                            className="mt-0.5 border-gray-600 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
                          />
                          <label htmlFor="marketing" className="text-xs text-gray-400 leading-relaxed cursor-pointer">
                            Send me promotions and special offers by text.
                          </label>
                        </div>
                      </div>

                      {/* TCPA Disclosure */}
                      <p className="text-[10px] text-gray-500 leading-relaxed">
                        Message & data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help. 
                        View our <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">Privacy Policy</a> & <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">Terms</a>.
                      </p>

                      <Button
                        type="submit"
                        className="w-full h-12 bg-gradient-to-r from-orange-600 to-amber-500 hover:from-orange-500 hover:to-amber-400 text-white font-bold rounded-xl shadow-lg"
                        disabled={isLoading || !formData.transactionalConsent}
                      >
                        {isLoading ? "Sending..." : <><Send className="w-4 h-4 mr-2" />Send Message</>}
                      </Button>

                      <div className="relative py-2">
                        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
                        <div className="relative flex justify-center text-xs"><span className="px-3 bg-[#181818] text-gray-500">or call</span></div>
                      </div>

                      <a href="tel:+14694040899" className="flex items-center justify-center gap-2 py-2 text-orange-400 hover:text-orange-300 font-semibold">
                        <Phone className="w-4 h-4" />469-404-0899
                      </a>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
